// Typed client for the Game Growth OS API. Used by the dashboard (browser) and the `growth` CLI (Node >= 22).
//
//   const api = new GrowthApi({ baseUrl: 'https://growth.example.com', token: process.env.GROWTH_TOKEN });
//   const overview = await api.overview('antpocalypse', { last: '7d' });

import type {
  ApiToken,
  Build,
  CompareResult,
  CreateEventDefinitionInput,
  CreateGameInput,
  DataHealth,
  DeletionResult,
  Defaults,
  Dimensions,
  Environment,
  EventDefinitions,
  EventPage,
  EventQuery,
  FilterParams,
  Game,
  IngestKey,
  InstallationDetail,
  InstallationExport,
  JobInfo,
  JobRun,
  JobRunResult,
  Me,
  Overview,
  RegisterBuildInput,
  RetentionGroupBy,
  RetentionResult,
  RunDetail,
  RunListQuery,
  RunRow,
  RunSummary,
  Activation,
  ResolvedFilters,
  SdkConfig,
  CustomEventDefinition,
  Scope,
  TimestampPolicy,
  UpdateGameInput,
} from './types.ts';
import type {
  Connection,
  ConnectionTest,
  CreateConnectionInput,
  Creative,
  CreativeConcept,
  CreativeDetail,
  CreativeInput,
  CreativeLibrary,
  CreativeLink,
  Funnel,
  FunnelReport,
  FunnelStepInfo,
  FunnelStepInput,
  LinkSuggestion,
  StoredTestReport,
  TestRecommendation,
  UaBreakdown,
  UaBreakdownBy,
  UaOverview,
  UaSummary,
  UaTest,
  UaTestInput,
  UaTestReport,
  UaTestVariant,
  UnmappedAsset,
  UpdateConnectionInput,
} from './acquisition.ts';

import type {
  LtvQuery,
  LtvResult,
  RevenueBasis,
  RevenueOverview,
  RevenueSummary,
  RoasBreakdown,
  RoasBy,
  RoasOverview,
  RoasQuery,
  Target,
  TargetMetric,
  UnitEconomics,
  UnitEconomicsByCountry,
} from './monetization.ts';

import type {
  AttributeOverview,
  CatalogReference,
  CreativeAsset,
  CreativeAttribute,
  CreativeCatalog,
  CreativeInsights,
  CreativeRender,
  CreativeSpec,
  Dimension,
  PlayableGroup,
  PlayableMetrics,
  Prompt,
  PromptDetail,
  PromptPurpose,
  RenderInput,
  SaveSpecResult,
  SpecChange,
  SpecRow,
  SpecValidation,
  SpecVersionInfo,
  Timeline,
  UploadMeta,
  VariationsResult,
} from './production.ts';

import type { Alert, AlertList, AlertRule, AlertRuleInput, AlertRules, Diagnosis } from './alerts.ts';
import type { GenerateReportInput, Report, ReportList } from './reports.ts';
import type { AiAnswer, AiDryRun, AiHypothesis, AiPack, AiRunDetail, AiRunList, AiStatus, CreativeHypothesesInput } from './ai.ts';
import type {
  Experiment,
  ExperimentInput,
  ExperimentPatch,
  ExperimentResults,
  ExperimentStatus,
  ParamChange,
  PublishResult,
  RemoteConfigPreview,
  RemoteConfigState,
  RemoteConfigValidation,
  RemoteConfigVersion,
  RemoteConfigVersionInfo,
  RemoteParams,
  StopExperimentInput,
} from './experiments.ts';

export * from './types.ts';
export * from './acquisition.ts';
export * from './monetization.ts';
export * from './production.ts';
export * from './filters.ts';
export * from './experiments.ts';
export * from './alerts.ts';
export * from './reports.ts';
export * from './ai.ts';

export interface ClientOptions {
  /** API origin, e.g. https://growth.example.com. Empty string = same origin (dashboard). */
  baseUrl?: string;
  /** Bearer token (ADMIN_TOKEN or an API token created with `growth token create`). */
  token?: string | null;
  fetch?: typeof fetch;
  /** Abort requests that take longer than this (default 60 s). */
  timeoutMs?: number;
  /** Extra headers on every request (e.g. User-Agent for the CLI). */
  headers?: Record<string, string>;
}

/** Error returned by the API: { error: code, detail }. status 0 = network error / timeout. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail: unknown;
  constructor(status: number, code: string, detail?: unknown) {
    super(ApiError.describe(status, code, detail));
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.detail = detail ?? null;
  }

  static describe(status: number, code: string, detail: unknown): string {
    const d = detail === undefined || detail === null ? '' : typeof detail === 'string' ? detail : JSON.stringify(detail);
    return `${status ? `HTTP ${status} ` : ''}${code}${d ? `: ${d}` : ''}`;
  }
}

type QueryValue = string | number | boolean | null | undefined | readonly (string | number)[];

/** Serializes a query object: skips empty values, joins arrays with commas. */
export function toQueryString(q: Record<string, QueryValue> | object | undefined): string {
  if (!q) return '';
  const parts: string[] = [];
  for (const [k, raw] of Object.entries(q as Record<string, QueryValue>)) {
    if (raw === undefined || raw === null || raw === '') continue;
    const v = Array.isArray(raw) ? raw.join(',') : String(raw);
    if (v === '') continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
  }
  return parts.length ? `?${parts.join('&')}` : '';
}

export interface RequestOptions {
  query?: object;
  body?: unknown;
  /** a non-JSON body (file upload): a Blob (e.g. fs.openAsBlob), bytes or a stream */
  raw?: { body: Blob | Uint8Array | ReadableStream<Uint8Array>; contentType: string };
  signal?: AbortSignal;
  /** overrides the client timeout (large uploads) */
  timeoutMs?: number;
}

export class GrowthApi {
  readonly baseUrl: string;
  private token: string | null;
  private readonly fetchImpl: typeof fetch;
  private readonly timeoutMs: number;
  private readonly headers: Record<string, string>;

  constructor(opts: ClientOptions = {}) {
    this.baseUrl = (opts.baseUrl ?? '').replace(/\/+$/, '');
    this.token = opts.token ?? null;
    this.fetchImpl = opts.fetch ?? globalThis.fetch.bind(globalThis);
    this.timeoutMs = opts.timeoutMs ?? 60_000;
    this.headers = opts.headers ?? {};
  }

  setToken(token: string | null): void {
    this.token = token;
  }

  get hasToken(): boolean {
    return !!this.token;
  }

  async request<T>(method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE', path: string, opts: RequestOptions = {}): Promise<T> {
    const url = `${this.baseUrl}${path}${toQueryString(opts.query)}`;
    const headers: Record<string, string> = { accept: 'application/json', ...this.headers };
    if (this.token) headers.authorization = `Bearer ${this.token}`;
    let body: RequestInit['body'];
    let duplex: 'half' | undefined;
    if (opts.raw) {
      headers['content-type'] = opts.raw.contentType;
      body = opts.raw.body as RequestInit['body'];
      if (typeof (opts.raw.body as ReadableStream).getReader === 'function') duplex = 'half'; // streamed request body
    } else if (opts.body !== undefined) {
      headers['content-type'] = 'application/json';
      body = JSON.stringify(opts.body);
    }
    const timeout = AbortSignal.timeout(opts.timeoutMs ?? this.timeoutMs);
    const signal = opts.signal ? AbortSignal.any([opts.signal, timeout]) : timeout;
    let res: Response;
    try {
      res = await this.fetchImpl(url, { method, headers, body, signal, ...(duplex ? { duplex } : {}) } as RequestInit);
    } catch (err) {
      if (opts.signal?.aborted) throw err;
      const e = err as Error;
      throw new ApiError(0, e.name === 'TimeoutError' ? 'timeout' : 'network_error', e.message);
    }
    const text = await res.text();
    let json: unknown = null;
    if (text) {
      try {
        json = JSON.parse(text);
      } catch {
        if (!res.ok) throw new ApiError(res.status, 'http_error', text.slice(0, 300));
        throw new ApiError(res.status, 'invalid_json', text.slice(0, 300));
      }
    }
    if (!res.ok) {
      const j = (json ?? {}) as { error?: string; detail?: unknown };
      throw new ApiError(res.status, j.error ?? 'http_error', j.detail);
    }
    return json as T;
  }

  private gamePath(gameId: string, rest = ''): string {
    return `/v1/games/${encodeURIComponent(gameId)}${rest}`;
  }

  // ------------------------------------------------------------ system

  health(): Promise<{ ok: boolean; version: string }> {
    return this.request('GET', '/health');
  }

  me(): Promise<Me> {
    return this.request('GET', '/v1/me');
  }

  // ------------------------------------------------------------ games & configuration

  async listGames(opts: { includeArchived?: boolean } = {}): Promise<Game[]> {
    const r = await this.request<{ games: Game[] }>('GET', '/v1/games', { query: { include_archived: opts.includeArchived ? 'true' : undefined } });
    return r.games;
  }

  createGame(input: CreateGameInput): Promise<{ game: Game; keys: IngestKey[] }> {
    return this.request('POST', '/v1/games', { body: input });
  }

  async getGame(gameId: string): Promise<Game> {
    return (await this.request<{ game: Game }>('GET', this.gamePath(gameId))).game;
  }

  updateGame(gameId: string, patch: UpdateGameInput): Promise<{ game: Game; notes: string[] }> {
    return this.request('PATCH', this.gamePath(gameId), { body: patch });
  }

  sdkConfig(gameId: string): Promise<SdkConfig> {
    return this.request('GET', this.gamePath(gameId, '/sdk-config'));
  }

  async listKeys(gameId: string): Promise<IngestKey[]> {
    return (await this.request<{ keys: IngestKey[] }>('GET', this.gamePath(gameId, '/keys'))).keys;
  }

  async createKey(gameId: string, input: { environment: Environment; label?: string; timestamp_policy?: TimestampPolicy }): Promise<IngestKey> {
    return (await this.request<{ key: IngestKey }>('POST', this.gamePath(gameId, '/keys'), { body: input })).key;
  }

  revokeKey(gameId: string, keyId: string): Promise<{ key: { id: string; revoked_at: string } }> {
    return this.request('POST', this.gamePath(gameId, `/keys/${encodeURIComponent(keyId)}/revoke`), { body: {} });
  }

  async listBuilds(gameId: string): Promise<Build[]> {
    return (await this.request<{ builds: Build[] }>('GET', this.gamePath(gameId, '/builds'))).builds;
  }

  async registerBuild(gameId: string, input: RegisterBuildInput): Promise<Build> {
    return (await this.request<{ build: Build }>('POST', this.gamePath(gameId, '/builds'), { body: input })).build;
  }

  eventDefinitions(gameId: string): Promise<EventDefinitions> {
    return this.request('GET', this.gamePath(gameId, '/event-definitions'));
  }

  async createEventDefinition(gameId: string, input: CreateEventDefinitionInput): Promise<CustomEventDefinition> {
    return (await this.request<{ definition: CustomEventDefinition }>('POST', this.gamePath(gameId, '/event-definitions'), { body: input })).definition;
  }

  catalog(): Promise<{ schema_version: string; packs: { name: string; description: string; events: unknown[] }[]; [key: string]: unknown }> {
    return this.request('GET', '/v1/catalog');
  }

  defaults(): Promise<Defaults> {
    return this.request('GET', '/v1/defaults');
  }

  async listTokens(): Promise<ApiToken[]> {
    return (await this.request<{ tokens: ApiToken[] }>('GET', '/v1/tokens')).tokens;
  }

  /** The secret is returned only once. */
  createToken(input: { name: string; scopes: Scope[] }): Promise<ApiToken & { token: string }> {
    return this.request('POST', '/v1/tokens', { body: input });
  }

  revokeToken(tokenId: string): Promise<{ token: { id: string; revoked_at: string } }> {
    return this.request('POST', `/v1/tokens/${encodeURIComponent(tokenId)}/revoke`, { body: {} });
  }

  // ------------------------------------------------------------ metrics

  overview(gameId: string, filters: FilterParams = {}, signal?: AbortSignal): Promise<Overview> {
    return this.request('GET', this.gamePath(gameId, '/metrics/overview'), { query: filters, signal });
  }

  compare(gameId: string, a: FilterParams, b: FilterParams, signal?: AbortSignal): Promise<CompareResult> {
    return this.request('POST', this.gamePath(gameId, '/metrics/compare'), { body: { a: stringifyFilters(a), b: stringifyFilters(b) }, signal });
  }

  retention(gameId: string, filters: FilterParams & { days?: number[]; group_by?: RetentionGroupBy } = {}, signal?: AbortSignal): Promise<RetentionResult> {
    return this.request('GET', this.gamePath(gameId, '/metrics/retention'), { query: filters, signal });
  }

  async activation(gameId: string, filters: FilterParams = {}, signal?: AbortSignal): Promise<{ filters: ResolvedFilters; activation: Activation }> {
    return this.request('GET', this.gamePath(gameId, '/metrics/activation'), { query: filters, signal });
  }

  async runs(gameId: string, query: RunListQuery = {}, signal?: AbortSignal): Promise<RunRow[]> {
    return (await this.request<{ runs: RunRow[] }>('GET', this.gamePath(gameId, '/runs'), { query, signal })).runs;
  }

  async runSummary(gameId: string, filters: FilterParams = {}, signal?: AbortSignal): Promise<RunSummary> {
    return (await this.request<{ summary: RunSummary }>('GET', this.gamePath(gameId, '/runs/summary'), { query: filters, signal })).summary;
  }

  run(gameId: string, runId: string, environment: Environment = 'production', signal?: AbortSignal): Promise<RunDetail> {
    return this.request('GET', this.gamePath(gameId, `/runs/${encodeURIComponent(runId)}`), { query: { environment }, signal });
  }

  installation(gameId: string, installationId: string, environment: Environment = 'production', signal?: AbortSignal): Promise<InstallationDetail> {
    return this.request('GET', this.gamePath(gameId, `/installations/${encodeURIComponent(installationId)}`), { query: { environment }, signal });
  }

  events(gameId: string, query: EventQuery = {}, signal?: AbortSignal): Promise<EventPage> {
    return this.request('GET', this.gamePath(gameId, '/events'), { query, signal });
  }

  dimensions(gameId: string, environment: Environment = 'production', signal?: AbortSignal): Promise<Dimensions> {
    return this.request('GET', this.gamePath(gameId, '/dimensions'), { query: { environment }, signal });
  }

  dataHealth(gameId: string, filters: FilterParams = {}, signal?: AbortSignal): Promise<DataHealth> {
    return this.request('GET', this.gamePath(gameId, '/data-health'), { query: filters, signal });
  }

  // ------------------------------------------------------------ privacy

  deleteInstallation(gameId: string, installationId: string): Promise<DeletionResult> {
    return this.request('POST', this.gamePath(gameId, '/privacy/delete'), { body: { installation_id: installationId } });
  }

  exportInstallation(gameId: string, installationId: string): Promise<InstallationExport> {
    return this.request('GET', this.gamePath(gameId, '/privacy/export'), { query: { installation_id: installationId } });
  }

  // ------------------------------------------------------------ jobs

  async listJobs(): Promise<JobInfo[]> {
    return (await this.request<{ jobs: JobInfo[] }>('GET', '/v1/jobs')).jobs;
  }

  async jobRuns(limit = 50): Promise<JobRun[]> {
    return (await this.request<{ runs: JobRun[] }>('GET', '/v1/jobs/runs', { query: { limit } })).runs;
  }

  runJob(name: string, params: Record<string, unknown> = {}): Promise<JobRunResult> {
    return this.request('POST', `/v1/jobs/${encodeURIComponent(name)}/run`, { body: params });
  }

  // ------------------------------------------------------------ acquisition connections (read-only connectors)

  async connections(gameId: string): Promise<Connection[]> {
    return (await this.request<{ connections: Connection[] }>('GET', this.gamePath(gameId, '/connections'))).connections;
  }

  async createConnection(gameId: string, input: CreateConnectionInput): Promise<Connection> {
    return (await this.request<{ connection: Connection }>('POST', this.gamePath(gameId, '/connections'), { body: input })).connection;
  }

  async updateConnection(gameId: string, connectionId: string, patch: UpdateConnectionInput): Promise<Connection> {
    return (await this.request<{ connection: Connection }>('PATCH', this.gamePath(gameId, `/connections/${encodeURIComponent(connectionId)}`), { body: patch })).connection;
  }

  deleteConnection(gameId: string, connectionId: string): Promise<{ deleted: boolean }> {
    return this.request('DELETE', this.gamePath(gameId, `/connections/${encodeURIComponent(connectionId)}`));
  }

  testConnection(gameId: string, connectionId: string): Promise<ConnectionTest> {
    return this.request('POST', this.gamePath(gameId, `/connections/${encodeURIComponent(connectionId)}/test`), { body: {} });
  }

  syncConnection(gameId: string, connectionId: string, opts: { from?: string; to?: string; force?: boolean } = {}): Promise<JobRunResult> {
    return this.request('POST', this.gamePath(gameId, `/connections/${encodeURIComponent(connectionId)}/sync`), { body: opts });
  }

  // ------------------------------------------------------------ UA metrics

  uaOverview(gameId: string, filters: FilterParams = {}, signal?: AbortSignal): Promise<UaOverview> {
    return this.request('GET', this.gamePath(gameId, '/ua/overview'), { query: filters, signal });
  }

  uaBreakdown(gameId: string, by: UaBreakdownBy, filters: FilterParams = {}, signal?: AbortSignal): Promise<UaBreakdown> {
    return this.request('GET', this.gamePath(gameId, '/ua/breakdown'), { query: { ...filters, by }, signal });
  }

  uaSummary(gameId: string, signal?: AbortSignal): Promise<UaSummary> {
    return this.request('GET', this.gamePath(gameId, '/ua/summary'), { signal });
  }

  // ------------------------------------------------------------ creative library

  async creativeConcepts(gameId: string): Promise<CreativeConcept[]> {
    return (await this.request<{ concepts: CreativeConcept[] }>('GET', this.gamePath(gameId, '/creative-concepts'))).concepts;
  }

  async createConcept(gameId: string, input: { concept_id: string; name: string; description?: string; hypothesis?: string; status?: CreativeConcept['status'] }): Promise<CreativeConcept> {
    return (await this.request<{ concept: CreativeConcept }>('POST', this.gamePath(gameId, '/creative-concepts'), { body: input })).concept;
  }

  async updateConcept(gameId: string, conceptId: string, patch: Partial<Pick<CreativeConcept, 'name' | 'description' | 'hypothesis' | 'status'>>): Promise<CreativeConcept> {
    return (await this.request<{ concept: CreativeConcept }>('PATCH', this.gamePath(gameId, `/creative-concepts/${encodeURIComponent(conceptId)}`), { body: patch })).concept;
  }

  creatives(gameId: string, filters: FilterParams & { include_retired?: boolean } = {}, signal?: AbortSignal): Promise<CreativeLibrary> {
    return this.request('GET', this.gamePath(gameId, '/creatives'), { query: filters, signal });
  }

  async createCreative(gameId: string, input: CreativeInput & { creative_id: string; name: string }): Promise<Creative> {
    return (await this.request<{ creative: Creative }>('POST', this.gamePath(gameId, '/creatives'), { body: input })).creative;
  }

  creative(gameId: string, creativeId: string, filters: FilterParams = {}, signal?: AbortSignal): Promise<CreativeDetail> {
    return this.request('GET', this.gamePath(gameId, `/creatives/${encodeURIComponent(creativeId)}`), { query: filters, signal });
  }

  async updateCreative(gameId: string, creativeId: string, patch: CreativeInput): Promise<Creative> {
    return (await this.request<{ creative: Creative }>('PATCH', this.gamePath(gameId, `/creatives/${encodeURIComponent(creativeId)}`), { body: patch })).creative;
  }

  async linkCreative(gameId: string, creativeId: string, link: { external_kind: CreativeLink['external_kind']; external_id: string; provider?: string }): Promise<CreativeLink> {
    return (await this.request<{ link: CreativeLink }>('POST', this.gamePath(gameId, `/creatives/${encodeURIComponent(creativeId)}/links`), { body: link })).link;
  }

  unlinkCreative(gameId: string, link: Pick<CreativeLink, 'provider' | 'external_kind' | 'external_id'>): Promise<{ deleted: boolean }> {
    const path = `/creative-links/${encodeURIComponent(link.provider)}/${encodeURIComponent(link.external_kind)}/${encodeURIComponent(link.external_id)}`;
    return this.request('DELETE', this.gamePath(gameId, path));
  }

  async linkSuggestions(gameId: string): Promise<LinkSuggestion[]> {
    return (await this.request<{ suggestions: LinkSuggestion[] }>('GET', this.gamePath(gameId, '/creative-links/suggestions'))).suggestions;
  }

  applyLinkSuggestions(gameId: string, items?: LinkSuggestion[]): Promise<{ created: number; links: LinkSuggestion[] }> {
    return this.request('POST', this.gamePath(gameId, '/creative-links/suggestions/apply'), { body: items ? { items } : {} });
  }

  async unmappedAssets(gameId: string, filters: FilterParams = {}, signal?: AbortSignal): Promise<UnmappedAsset[]> {
    return (await this.request<{ assets: UnmappedAsset[] }>('GET', this.gamePath(gameId, '/creative-links/unmapped'), { query: filters, signal })).assets;
  }

  // ------------------------------------------------------------ creative production (Phase 4)

  creativeCatalog(gameId: string, signal?: AbortSignal): Promise<{ catalog: CreativeCatalog; reference: CatalogReference }> {
    return this.request('GET', this.gamePath(gameId, '/creative-catalog'), { signal });
  }

  async setCreativeCatalog(gameId: string, catalog: CreativeCatalog): Promise<CreativeCatalog> {
    return (await this.request<{ catalog: CreativeCatalog }>('PUT', this.gamePath(gameId, '/creative-catalog'), { body: catalog })).catalog;
  }

  validateSpec(gameId: string, spec: unknown): Promise<SpecValidation> {
    return this.request('POST', this.gamePath(gameId, '/creative-specs/validate'), { body: { spec } });
  }

  specs(gameId: string, creativeId: string, signal?: AbortSignal): Promise<{ versions: SpecVersionInfo[]; latest: SpecRow | null }> {
    return this.request('GET', this.gamePath(gameId, `/creatives/${encodeURIComponent(creativeId)}/specs`), { signal });
  }

  spec(gameId: string, creativeId: string, version: number | 'latest' = 'latest', signal?: AbortSignal): Promise<{ spec: SpecRow; timeline: Timeline; changes_from_previous: SpecChange[] | null }> {
    return this.request('GET', this.gamePath(gameId, `/creatives/${encodeURIComponent(creativeId)}/specs/${version}`), { signal });
  }

  /** Saves a new spec version (a spec identical to the latest one returns it with created: false). */
  saveSpec(gameId: string, creativeId: string, spec: CreativeSpec | unknown, note?: string): Promise<SaveSpecResult> {
    return this.request('POST', this.gamePath(gameId, `/creatives/${encodeURIComponent(creativeId)}/specs`), { body: { spec, note } });
  }

  variations(gameId: string, creativeId: string, input: { count?: number; dimensions?: Dimension[]; dry_run?: boolean; ids?: string[] } = {}): Promise<VariationsResult> {
    return this.request('POST', this.gamePath(gameId, `/creatives/${encodeURIComponent(creativeId)}/variations`), { body: input });
  }

  async renders(gameId: string, creativeId: string, signal?: AbortSignal): Promise<CreativeRender[]> {
    return (await this.request<{ renders: CreativeRender[] }>('GET', this.gamePath(gameId, `/creatives/${encodeURIComponent(creativeId)}/renders`), { signal })).renders;
  }

  async render(renderId: string, signal?: AbortSignal): Promise<CreativeRender> {
    return (await this.request<{ render: CreativeRender }>('GET', `/v1/creative-renders/${encodeURIComponent(renderId)}`, { signal })).render;
  }

  async createRender(gameId: string, creativeId: string, input: RenderInput): Promise<CreativeRender> {
    return (await this.request<{ render: CreativeRender }>('POST', this.gamePath(gameId, `/creatives/${encodeURIComponent(creativeId)}/renders`), { body: input })).render;
  }

  /** Uploads one file of a render. In Node, pass `await fs.openAsBlob(path)` to stream it from disk. */
  async uploadRenderFile(renderId: string, name: string, body: Blob | Uint8Array | ReadableStream<Uint8Array>, meta: UploadMeta = {}, opts: { timeoutMs?: number } = {}) {
    const path = `/v1/creative-renders/${encodeURIComponent(renderId)}/files/${encodeURIComponent(name)}`;
    return (
      await this.request<{ asset: CreativeAsset }>('PUT', path, {
        query: meta,
        raw: { body, contentType: 'application/octet-stream' },
        timeoutMs: opts.timeoutMs ?? 30 * 60_000,
      })
    ).asset;
  }

  completeRender(renderId: string, input: { status: 'ready' | 'failed'; manifest?: Record<string, unknown>; error?: string }): Promise<{ render: CreativeRender; files: number }> {
    return this.request('POST', `/v1/creative-renders/${encodeURIComponent(renderId)}/complete`, { body: input });
  }

  deleteRender(renderId: string): Promise<{ deleted: boolean }> {
    return this.request('DELETE', `/v1/creative-renders/${encodeURIComponent(renderId)}`);
  }

  /** Absolute URL of a signed file link returned by the API (relative to its origin). */
  fileUrl(url: string): string {
    return /^https?:\/\//.test(url) ? url : `${this.baseUrl}${url}`;
  }

  async creativeAttributes(gameId: string, creativeId: string): Promise<CreativeAttribute[]> {
    return (await this.request<{ attributes: CreativeAttribute[] }>('GET', this.gamePath(gameId, `/creatives/${encodeURIComponent(creativeId)}/attributes`))).attributes;
  }

  setCreativeAttribute(gameId: string, creativeId: string, key: string, value: string | number | boolean): Promise<{ attribute: { key: string; value: unknown; source: 'manual' } }> {
    return this.request('PUT', this.gamePath(gameId, `/creatives/${encodeURIComponent(creativeId)}/attributes/${encodeURIComponent(key)}`), { body: { value } });
  }

  deleteCreativeAttribute(gameId: string, creativeId: string, key: string): Promise<{ deleted: boolean }> {
    return this.request('DELETE', this.gamePath(gameId, `/creatives/${encodeURIComponent(creativeId)}/attributes/${encodeURIComponent(key)}`));
  }

  async attributeOverview(gameId: string, signal?: AbortSignal): Promise<AttributeOverview[]> {
    return (await this.request<{ attributes: AttributeOverview[] }>('GET', this.gamePath(gameId, '/creative-insights'), { signal })).attributes;
  }

  creativeInsights(gameId: string, query: FilterParams & { attribute: string; buckets?: string | number[]; by_country?: boolean; include_retired?: boolean }, signal?: AbortSignal): Promise<CreativeInsights> {
    return this.request('GET', this.gamePath(gameId, '/creative-insights'), { query, signal });
  }

  /** Playable funnel (beacon) and, per creative, the network's spend, engagements, CPE and engagement → install. */
  playableMetrics(gameId: string, query: FilterParams & { group_by?: PlayableGroup; ad_network?: string } = {}, signal?: AbortSignal): Promise<PlayableMetrics> {
    return this.request('GET', this.gamePath(gameId, '/playable-metrics'), { query, signal });
  }

  async prompts(gameId: string, signal?: AbortSignal): Promise<Prompt[]> {
    return (await this.request<{ prompts: Prompt[] }>('GET', this.gamePath(gameId, '/prompts'), { signal })).prompts;
  }

  prompt(gameId: string, promptId: string, filters: FilterParams = {}, signal?: AbortSignal): Promise<PromptDetail> {
    return this.request('GET', this.gamePath(gameId, `/prompts/${encodeURIComponent(promptId)}`), { query: filters, signal });
  }

  createPrompt(gameId: string, input: { prompt_id: string; title: string; text: string; purpose?: PromptPurpose; model?: string; params?: Record<string, unknown> }): Promise<{ prompt_id: string; version: number }> {
    return this.request('POST', this.gamePath(gameId, '/prompts'), { body: input });
  }

  addPromptVersion(gameId: string, promptId: string, input: { text: string; model?: string; params?: Record<string, unknown> }): Promise<{ prompt_id: string; version: number; created: boolean }> {
    return this.request('POST', this.gamePath(gameId, `/prompts/${encodeURIComponent(promptId)}/versions`), { body: input });
  }

  // ------------------------------------------------------------ funnels

  async funnelSteps(): Promise<FunnelStepInfo[]> {
    return (await this.request<{ steps: FunnelStepInfo[] }>('GET', '/v1/funnel-steps')).steps;
  }

  async funnels(gameId: string): Promise<Funnel[]> {
    return (await this.request<{ funnels: Funnel[] }>('GET', this.gamePath(gameId, '/funnels'))).funnels;
  }

  async createFunnel(gameId: string, input: { funnel_id: string; name: string; steps: (FunnelStepInput | string)[] }): Promise<Funnel> {
    return (await this.request<{ funnel: Funnel }>('POST', this.gamePath(gameId, '/funnels'), { body: input })).funnel;
  }

  async updateFunnel(gameId: string, funnelId: string, patch: { name?: string; steps?: (FunnelStepInput | string)[] }): Promise<Funnel> {
    return (await this.request<{ funnel: Funnel }>('PATCH', this.gamePath(gameId, `/funnels/${encodeURIComponent(funnelId)}`), { body: patch })).funnel;
  }

  deleteFunnel(gameId: string, funnelId: string): Promise<{ deleted: boolean }> {
    return this.request('DELETE', this.gamePath(gameId, `/funnels/${encodeURIComponent(funnelId)}`));
  }

  funnelReport(gameId: string, funnelId = 'default', filters: FilterParams = {}, signal?: AbortSignal): Promise<FunnelReport> {
    return this.request('GET', this.gamePath(gameId, `/funnels/${encodeURIComponent(funnelId)}/report`), { query: filters, signal });
  }

  // ------------------------------------------------------------ Test Lab

  async uaTests(gameId: string): Promise<UaTest[]> {
    return (await this.request<{ tests: UaTest[] }>('GET', this.gamePath(gameId, '/ua-tests'))).tests;
  }

  createUaTest(gameId: string, input: UaTestInput): Promise<{ test: UaTest; variants: UaTestVariant[] }> {
    return this.request('POST', this.gamePath(gameId, '/ua-tests'), { body: input });
  }

  /** `testId`: uuid or the per-game number (TEST #3 -> "3"). */
  uaTest(gameId: string, testId: string | number): Promise<{ test: UaTest; variants: UaTestVariant[]; reports: { id: number; generated_at: string; recommendation: string }[] }> {
    return this.request('GET', this.gamePath(gameId, `/ua-tests/${encodeURIComponent(String(testId))}`));
  }

  updateUaTest(gameId: string, testId: string | number, patch: Partial<UaTestInput>): Promise<{ test: UaTest; variants: UaTestVariant[] }> {
    return this.request('PATCH', this.gamePath(gameId, `/ua-tests/${encodeURIComponent(String(testId))}`), { body: patch });
  }

  uaTestEvaluation(gameId: string, testId: string | number, signal?: AbortSignal): Promise<UaTestReport> {
    return this.request('GET', this.gamePath(gameId, `/ua-tests/${encodeURIComponent(String(testId))}/evaluation`), { signal });
  }

  generateUaTestReport(gameId: string, testId: string | number): Promise<StoredTestReport> {
    return this.request('POST', this.gamePath(gameId, `/ua-tests/${encodeURIComponent(String(testId))}/report`), { body: {} });
  }

  uaTestReport(gameId: string, testId: string | number, reportId: number): Promise<StoredTestReport> {
    return this.request('GET', this.gamePath(gameId, `/ua-tests/${encodeURIComponent(String(testId))}/reports/${reportId}`));
  }

  decideUaTest(gameId: string, testId: string | number, decision: TestRecommendation, note?: string): Promise<{ test: UaTest; note: string }> {
    return this.request('POST', this.gamePath(gameId, `/ua-tests/${encodeURIComponent(String(testId))}/decision`), { body: { decision, ...(note ? { note } : {}) } });
  }

  // ------------------------------------------------------------ monetization (Phase 3)

  /** Ad + IAP revenue, ARPDAU / ARPU / ARPPU, payer conversion, impressions per DAU, eCPM, rewarded opt-in, breakdowns. */
  revenueOverview(gameId: string, filters: FilterParams & { basis?: RevenueBasis } = {}, signal?: AbortSignal): Promise<RevenueOverview> {
    return this.request('GET', this.gamePath(gameId, '/revenue/overview'), { query: filters, signal });
  }

  /** Home tiles: revenue 7 days, ARPDAU, LTV D7, ROAS D7. */
  revenueSummary(gameId: string, signal?: AbortSignal): Promise<RevenueSummary> {
    return this.request('GET', this.gamePath(gameId, '/revenue/summary'), { signal });
  }

  /** Observed LTV per install cohort (D0…D90), cumulative curve and a separate ESTIMATED projection. */
  ltv(gameId: string, query: LtvQuery = {}, signal?: AbortSignal): Promise<LtvResult> {
    return this.request('GET', this.gamePath(gameId, '/ltv'), { query, signal });
  }

  /** ROAS D0…D30 (shared / blended), payback (observed + ESTIMATED) and the Google Ads conversion value. Production only. */
  roas(gameId: string, query: RoasQuery = {}, signal?: AbortSignal): Promise<RoasOverview> {
    return this.request('GET', this.gamePath(gameId, '/roas'), { query, signal });
  }

  roasBreakdown(gameId: string, by: RoasBy, query: RoasQuery & { day?: number } = {}, signal?: AbortSignal): Promise<RoasBreakdown> {
    return this.request('GET', this.gamePath(gameId, '/roas/breakdown'), { query: { ...query, by }, signal });
  }

  /** Unit economics against the game targets (RED / YELLOW / GREEN). */
  economics(gameId: string, query: RoasQuery = {}, signal?: AbortSignal): Promise<UnitEconomics> {
    return this.request('GET', this.gamePath(gameId, '/economics'), { query: { ...query, group_by: 'none' }, signal });
  }

  economicsByCountry(gameId: string, query: RoasQuery = {}, signal?: AbortSignal): Promise<UnitEconomicsByCountry> {
    return this.request('GET', this.gamePath(gameId, '/economics'), { query: { ...query, group_by: 'country' }, signal });
  }

  targets(gameId: string): Promise<{ targets: Target[]; metrics: Record<string, TargetMetric> }> {
    return this.request('GET', this.gamePath(gameId, '/targets'));
  }

  /** GREEN at or better than `green`, RED worse than `red`; rates as fractions (0.35 = 35 %). */
  async setTarget(gameId: string, metric: string, target: { green: number; red: number; note?: string | null }): Promise<Target> {
    return (await this.request<{ target: Target }>('PUT', this.gamePath(gameId, `/targets/${encodeURIComponent(metric)}`), { body: target })).target;
  }

  deleteTarget(gameId: string, metric: string): Promise<{ deleted: boolean }> {
    return this.request('DELETE', this.gamePath(gameId, `/targets/${encodeURIComponent(metric)}`));
  }
  // ---------------------------------------------------------------- remote config (Phase 6)

  async remoteConfig(gameId: string, environment?: Environment, signal?: AbortSignal): Promise<RemoteConfigState> {
    return this.request('GET', this.gamePath(gameId, '/remote-config'), { query: { environment }, signal });
  }

  async remoteConfigVersions(gameId: string, environment?: Environment, signal?: AbortSignal): Promise<RemoteConfigVersionInfo[]> {
    return (await this.request<{ versions: RemoteConfigVersionInfo[] }>('GET', this.gamePath(gameId, '/remote-config/versions'), { query: { environment }, signal })).versions;
  }

  remoteConfigVersion(gameId: string, version: number, environment?: Environment, signal?: AbortSignal): Promise<{ config: RemoteConfigVersion; changes_from_previous: ParamChange[] }> {
    return this.request('GET', this.gamePath(gameId, `/remote-config/versions/${encodeURIComponent(String(version))}`), { query: { environment }, signal });
  }

  validateRemoteConfig(gameId: string, params: RemoteParams | unknown, environment?: Environment): Promise<RemoteConfigValidation> {
    return this.request('POST', this.gamePath(gameId, '/remote-config/validate'), { body: { environment, params } });
  }

  /** Publishes a new version; `base_version` (the version the edit started from) refuses it if somebody published meanwhile. */
  publishRemoteConfig(gameId: string, params: RemoteParams | unknown, opts: { environment?: Environment; note?: string; base_version?: number } = {}): Promise<PublishResult> {
    return this.request('POST', this.gamePath(gameId, '/remote-config/versions'), { body: { ...opts, params } });
  }

  rollbackRemoteConfig(gameId: string, version: number, opts: { environment?: Environment; note?: string } = {}): Promise<PublishResult> {
    return this.request('POST', this.gamePath(gameId, '/remote-config/rollback'), { body: { ...opts, version } });
  }

  /** What one device receives, and why it is in or out of each running experiment. */
  previewRemoteConfig(gameId: string, query: { installation_id: string; platform?: string; app_version?: string; environment?: Environment }, signal?: AbortSignal): Promise<RemoteConfigPreview> {
    return this.request('GET', this.gamePath(gameId, '/remote-config/preview'), { query, signal });
  }

  // ---------------------------------------------------------------- experiments (Phase 6)

  /** `ref`: the experiment's uuid, or its key (in `environment`, production by default). */
  async experiments(gameId: string, query: { environment?: Environment; status?: ExperimentStatus } = {}, signal?: AbortSignal): Promise<Experiment[]> {
    return (await this.request<{ experiments: Experiment[] }>('GET', this.gamePath(gameId, '/experiments'), { query, signal })).experiments;
  }

  async experiment(gameId: string, ref: string, environment?: Environment, signal?: AbortSignal): Promise<Experiment> {
    return (await this.request<{ experiment: Experiment }>('GET', this.gamePath(gameId, `/experiments/${encodeURIComponent(ref)}`), { query: { environment }, signal })).experiment;
  }

  createExperiment(gameId: string, input: ExperimentInput): Promise<{ experiment: Experiment; warnings: string[] }> {
    return this.request('POST', this.gamePath(gameId, '/experiments'), { body: input });
  }

  updateExperiment(gameId: string, ref: string, patch: ExperimentPatch, environment?: Environment): Promise<{ experiment: Experiment; warnings: string[] }> {
    return this.request('PATCH', this.gamePath(gameId, `/experiments/${encodeURIComponent(ref)}`), { query: { environment }, body: patch });
  }

  deleteExperiment(gameId: string, ref: string, environment?: Environment): Promise<{ deleted: boolean }> {
    return this.request('DELETE', this.gamePath(gameId, `/experiments/${encodeURIComponent(ref)}`), { query: { environment } });
  }

  async startExperiment(gameId: string, ref: string, environment?: Environment): Promise<Experiment> {
    return (await this.request<{ experiment: Experiment }>('POST', this.gamePath(gameId, `/experiments/${encodeURIComponent(ref)}/start`), { query: { environment }, body: {} })).experiment;
  }

  stopExperiment(gameId: string, ref: string, input: StopExperimentInput = {}, environment?: Environment): Promise<{ experiment: Experiment; config: RemoteConfigVersion | null; changes: ParamChange[] }> {
    return this.request('POST', this.gamePath(gameId, `/experiments/${encodeURIComponent(ref)}/stop`), { query: { environment }, body: input });
  }

  experimentResults(gameId: string, ref: string, environment?: Environment, signal?: AbortSignal): Promise<{ experiment: Experiment; results: ExperimentResults }> {
    return this.request('GET', this.gamePath(gameId, `/experiments/${encodeURIComponent(ref)}/results`), { query: { environment }, signal });
  }

  // ---------------------------------------------------------------- diagnostics & alerts (Phase 6)

  /** Growth scorecard and automatic diagnosis (production; the selector's period, 14 days by default). */
  diagnostics(gameId: string, filters: FilterParams = {}, signal?: AbortSignal): Promise<Diagnosis> {
    return this.request('GET', this.gamePath(gameId, '/diagnostics'), { query: filters, signal });
  }

  alerts(gameId: string, query: { status?: 'open' | 'resolved' | 'all'; environment?: Environment; limit?: number } = {}, signal?: AbortSignal): Promise<AlertList> {
    return this.request('GET', this.gamePath(gameId, '/alerts'), { query, signal });
  }

  async acknowledgeAlert(gameId: string, alertId: number): Promise<Alert> {
    return (await this.request<{ alert: Alert }>('POST', this.gamePath(gameId, `/alerts/${encodeURIComponent(String(alertId))}/ack`), { body: {} })).alert;
  }

  /** Runs the evaluation of this game's rules now (the worker does it every hour). */
  evaluateAlerts(gameId: string): Promise<JobRunResult> {
    return this.request('POST', this.gamePath(gameId, '/alerts/evaluate'), { body: {} });
  }

  alertRules(gameId: string, environment?: Environment, signal?: AbortSignal): Promise<AlertRules> {
    return this.request('GET', this.gamePath(gameId, '/alert-rules'), { query: { environment }, signal });
  }

  async createAlertRule(gameId: string, input: AlertRuleInput): Promise<AlertRule> {
    return (await this.request<{ rule: AlertRule }>('POST', this.gamePath(gameId, '/alert-rules'), { body: input })).rule;
  }

  async updateAlertRule(gameId: string, ruleId: string, patch: Partial<AlertRuleInput>): Promise<AlertRule> {
    return (await this.request<{ rule: AlertRule }>('PATCH', this.gamePath(gameId, `/alert-rules/${encodeURIComponent(ruleId)}`), { body: patch })).rule;
  }

  deleteAlertRule(gameId: string, ruleId: string): Promise<{ deleted: boolean }> {
    return this.request('DELETE', this.gamePath(gameId, `/alert-rules/${encodeURIComponent(ruleId)}`));
  }

  /** Adds the recommended rules that are not there yet. */
  addRecommendedAlertRules(gameId: string, environment?: Environment): Promise<{ created: AlertRule[]; skipped: number }> {
    return this.request('POST', this.gamePath(gameId, '/alert-rules/recommended'), { body: { environment } });
  }

  // ---------------------------------------------------------------- reports (Phase 6)

  /** Weekly reports, newest first, with their headline numbers. */
  reports(gameId: string, query: { limit?: number } = {}, signal?: AbortSignal): Promise<ReportList> {
    return this.request('GET', this.gamePath(gameId, '/reports'), { query: { kind: 'weekly', ...query }, signal });
  }

  /** A report by number or ISO week ('2026-W41'): structured content and Markdown. */
  async report(gameId: string, ref: number | string, signal?: AbortSignal): Promise<Report> {
    return (await this.request<{ report: Report }>('GET', this.gamePath(gameId, `/reports/${encodeURIComponent(String(ref))}`), { signal })).report;
  }

  /** Builds a weekly report now (an existing one is returned unless force). */
  generateReport(gameId: string, input: GenerateReportInput = {}): Promise<{ report: Report; created: boolean; replaced: boolean }> {
    return this.request('POST', this.gamePath(gameId, '/reports'), { body: { kind: 'weekly', ...input }, timeoutMs: 120_000 });
  }

  deleteReport(gameId: string, ref: number | string): Promise<{ deleted: boolean }> {
    return this.request('DELETE', this.gamePath(gameId, `/reports/${encodeURIComponent(String(ref))}`));
  }

  // ---------------------------------------------------------------- creative AI (Phase 6)

  aiStatus(gameId: string, signal?: AbortSignal): Promise<AiStatus> {
    return this.request('GET', this.gamePath(gameId, '/ai/status'), { signal });
  }

  /** Asks the creative agent for hypotheses (a run stored with its input and answer); dry_run only builds what would be sent. */
  proposeCreativeHypotheses(gameId: string, input: CreativeHypothesesInput & { dry_run: true }): Promise<AiDryRun>;
  proposeCreativeHypotheses(gameId: string, input?: CreativeHypothesesInput): Promise<AiRunDetail>;
  proposeCreativeHypotheses(gameId: string, input: CreativeHypothesesInput = {}): Promise<AiRunDetail | AiDryRun> {
    return this.request('POST', this.gamePath(gameId, '/ai/creative-hypotheses'), { body: input, timeoutMs: 600_000 });
  }

  /** The pack and instructions for a Claude app of the team to answer (no ANTHROPIC_API_KEY needed). */
  aiPack(gameId: string, input: Omit<CreativeHypothesesInput, 'dry_run'> = {}): Promise<AiPack> {
    return this.request('POST', this.gamePath(gameId, '/ai/packs'), { body: input });
  }

  /** The answer to a pack: validated like an API answer and stored as a run. */
  answerPack(gameId: string, packId: number, answer: AiAnswer): Promise<AiRunDetail> {
    return this.request('POST', this.gamePath(gameId, `/ai/packs/${encodeURIComponent(String(packId))}/answer`), { body: answer });
  }

  aiRuns(gameId: string, query: { limit?: number } = {}, signal?: AbortSignal): Promise<AiRunList> {
    return this.request('GET', this.gamePath(gameId, '/ai/runs'), { query, signal });
  }

  aiRun(gameId: string, runId: number, signal?: AbortSignal): Promise<AiRunDetail> {
    return this.request('GET', this.gamePath(gameId, `/ai/runs/${encodeURIComponent(String(runId))}`), { signal });
  }

  /** Makes the hypothesis' variations as draft creatives (the rule-based generator applies each change). */
  applyHypothesis(gameId: string, hypothesisId: number): Promise<{ hypothesis: AiHypothesis; created: { creative_id: string; name: string; dimension: string; option: string; label: string }[]; skipped: string[] }> {
    return this.request('POST', this.gamePath(gameId, `/ai/hypotheses/${encodeURIComponent(String(hypothesisId))}/apply`), { body: {} });
  }

  async dismissHypothesis(gameId: string, hypothesisId: number, note?: string): Promise<AiHypothesis> {
    return (await this.request<{ hypothesis: AiHypothesis }>('POST', this.gamePath(gameId, `/ai/hypotheses/${encodeURIComponent(String(hypothesisId))}/dismiss`), { body: { note } })).hypothesis;
  }
}

/** The compare endpoint takes filters as a JSON body: same keys as the query string, string values. */
function stringifyFilters(f: FilterParams): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(f)) {
    if (v === undefined || v === null || v === '') continue;
    out[k] = String(v);
  }
  return out;
}
